import { useDisclosure } from '@mantine/hooks';
import { ReactNode, useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFamilyCashflows } from 'src/non-financial-data/v2/family/useFamilyCashflows';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { showError, showSuccess } from 'src/toasts/toasts';
import { GroupMember } from '../grouping';
import useExpenditures from '../hooks/useExpenditures';
import useIncomes from '../hooks/useIncomes';
import useLifeEvents from '../hooks/useLifeEvents';
import {
  ExpenditureRow,
  IncomeRow,
  LifeEventRow,
  buildExpenditureRows,
  buildIncomeRows,
  buildLifeEventRows,
} from '../rows';
import { Period } from '../utils';
import { CashflowsLayout } from './CashflowsLayout';
import { ExpenditureFormModal } from './ExpenditureFormModal';
import { IncomeFormModal } from './IncomeFormModal';
import { LifeEventFormModal } from './LifeEventFormModal';
import {
  useDeleteExpenditure,
  useDeleteIncome,
  useDeleteLifeEvent,
  useSaveExpenditure,
  useSaveIncome,
  useSaveLifeEvent,
} from './useCashflowSaves';
import { OpenedItem, wholeItem } from './wholeItem';
import { memberIndex, toGroupMembers } from './useCashflowMembers';
import { reportSaveFailure } from './reportSaveFailure';

/**
 * The table on an individual client's tab reads only that client's rows, so a shared item shows
 * the member's own share and names them alone under "Applies to" — that is what a client's tab is
 * for. Opening an item is different: an adviser editing or deleting one has to see everyone it
 * reaches, so the family's rows are read alongside and the form is handed the whole item rather
 * than this member's slice of it. The form is then no longer a partial view of the item, so it
 * can rewrite membership and delete the item outright.
 *
 * Where the family's rows cannot be vouched for — a member whose read failed, so the item is open
 * on fewer members than share it — the form falls back to the partial view a client's tab held
 * before: shared fields and membership read-only, and no Delete, since a delete there would take
 * some copies while the warning named fewer members than lose it.
 */
export function ClientCashflowsRedesign({ modelsSection }: { modelsSection: ReactNode }) {
  const { clientId, client } = useCurrentClient();
  const { isLoading: loadingIncomes, incomesData } = useIncomes();
  const { isLoading: loadingExpenditures, expendituresData } = useExpenditures();
  const { lifeEventsData } = useLifeEvents();
  const { members: familyMembers, isLoading: loadingFamilyMembers } = useFamilyMembers();

  const [period, setPeriod] = useState<Period>('monthly');
  const [incomeOpen, income] = useDisclosure(false);
  const [expenditureOpen, expenditure] = useDisclosure(false);
  const [lifeEventOpen, lifeEvent] = useDisclosure(false);
  const [editedIncome, setEditedIncome] = useState<OpenedItem<IncomeRow> | undefined>();
  const [editedExpenditure, setEditedExpenditure] = useState<
    OpenedItem<ExpenditureRow> | undefined
  >();
  const [editedLifeEvent, setEditedLifeEvent] = useState<OpenedItem<LifeEventRow> | undefined>();

  const saveIncome = useSaveIncome();
  const saveExpenditure = useSaveExpenditure();
  const saveLifeEvent = useSaveLifeEvent();
  const deleteIncome = useDeleteIncome();
  const deleteExpenditure = useDeleteExpenditure();
  const deleteLifeEvent = useDeleteLifeEvent();

  // The table below reads this client alone, but opening an item has to show every member on it
  // and deleting it has to reach every member's copy, so the whole family's rows are read too.
  const family = useFamilyCashflows(familyMembers);

  const self: GroupMember = {
    clientId,
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
  };
  // A client with no family can still record cashflows — against themselves alone.
  const pickableMembers = familyMembers.length > 0 ? toGroupMembers(familyMembers) : [self];
  const byClientId = memberIndex(
    pickableMembers.some(member => member.clientId === clientId)
      ? pickableMembers
      : [self, ...pickableMembers],
  );

  // `useFamilyCashflows` has no queries to report on until the members it reads are resolved, so
  // waiting on it alone would paint the page — and open items as slices — while they are still in
  // flight.
  if (loadingIncomes || loadingExpenditures || loadingFamilyMembers || family.isLoading) {
    return <PageLoader absolute={false} />;
  }

  const save = <T,>(
    persist: (submission: T) => Promise<void>,
    noun: string,
    close: () => void,
    clearEdited: () => void,
  ) => {
    return async (submission: T) => {
      try {
        await persist(submission);
        showSuccess(`${noun} saved successfully`);
        close();
        clearEdited();
      } catch (error) {
        if (reportSaveFailure(error, byClientId, noun)) {
          close();
          clearEdited();
          return;
        }
        showError(`Failed to save ${noun.toLowerCase()}`);
      }
    };
  };

  // Keyed the same way the table's rows are, so a row the adviser clicks finds the item it is a
  // slice of. Built from the family read, which holds every member's copy.
  const wholeIncomeRows = buildIncomeRows(family.incomes, byClientId);
  const wholeExpenditureRows = buildExpenditureRows(family.expenditures, byClientId);
  const wholeLifeEventRows = buildLifeEventRows(family.lifeEvents, byClientId);

  const remove = (
    discard: (items: { id: string; clientId: string }[]) => Promise<void>,
    noun: string,
    close: () => void,
    clearEdited: () => void,
  ) => {
    return async (items: { id: string; clientId: string }[]) => {
      try {
        await discard(items);
        showSuccess(`${noun} deleted`);
        close();
        clearEdited();
      } catch (error) {
        // Part of a shared item going and part staying is reported by name and closes the form;
        // nothing going at all is left for the warning dialog to hold open and say so.
        if (reportSaveFailure(error, byClientId, noun)) {
          close();
          clearEdited();
          return;
        }
        throw error;
      }
    };
  };

  return (
    <>
      <CashflowsLayout
        period={period}
        onPeriodChange={setPeriod}
        incomeRows={buildIncomeRows(incomesData ?? [], byClientId)}
        expenditureRows={buildExpenditureRows(expendituresData ?? [], byClientId)}
        lifeEventRows={buildLifeEventRows(lifeEventsData ?? [], byClientId)}
        incomeActions={{
          onAdd: () => {
            setEditedIncome(undefined);
            income.open();
          },
          onRowClick: row => {
            setEditedIncome(wholeItem(row, wholeIncomeRows, family.hasErrors));
            income.open();
          },
        }}
        expenditureActions={{
          onAdd: () => {
            setEditedExpenditure(undefined);
            expenditure.open();
          },
          onRowClick: row => {
            setEditedExpenditure(wholeItem(row, wholeExpenditureRows, family.hasErrors));
            expenditure.open();
          },
        }}
        lifeEventActions={{
          onAdd: () => {
            setEditedLifeEvent(undefined);
            lifeEvent.open();
          },
          onRowClick: row => {
            setEditedLifeEvent(wholeItem(row, wholeLifeEventRows, family.hasErrors));
            lifeEvent.open();
          },
        }}
        modelsSection={modelsSection}
      />

      <IncomeFormModal
        opened={incomeOpen}
        onClose={income.close}
        onSubmit={save(saveIncome, 'Income', income.close, () => setEditedIncome(undefined))}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedIncome?.row}
        ownRowsOnly={editedIncome?.isPartial}
        onDelete={remove(deleteIncome, 'Income', income.close, () => setEditedIncome(undefined))}
      />
      <ExpenditureFormModal
        opened={expenditureOpen}
        onClose={expenditure.close}
        onSubmit={save(saveExpenditure, 'Expenditure', expenditure.close, () =>
          setEditedExpenditure(undefined),
        )}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedExpenditure?.row}
        ownRowsOnly={editedExpenditure?.isPartial}
        onDelete={remove(deleteExpenditure, 'Expenditure', expenditure.close, () =>
          setEditedExpenditure(undefined),
        )}
      />
      <LifeEventFormModal
        opened={lifeEventOpen}
        onClose={lifeEvent.close}
        onSubmit={save(saveLifeEvent, 'Life event', lifeEvent.close, () =>
          setEditedLifeEvent(undefined),
        )}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedLifeEvent?.row}
        ownRowsOnly={editedLifeEvent?.isPartial}
        onDelete={remove(deleteLifeEvent, 'Life event', lifeEvent.close, () =>
          setEditedLifeEvent(undefined),
        )}
      />
    </>
  );
}
